import { useRepositoryMutation, UserProfileResult } from '@mews-commander/data-access';
import { LocalizedText, showErrorAlert, showSuccessAlert } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { useUserContext } from '../../context';
import { UserType } from '../../types';

const useEnterpriseProfilesDelete = ({ fullName }: { fullName: string }) =>
    useRepositoryMutation('enterpriseProfiles', 'delete', {
        transformParams: (enterpriseProfileIds: string[]) => ({
            enterpriseProfileIds: [enterpriseProfileIds[0]],
        }),
        onSuccess: () => {
            showSuccessAlert({
                description: (
                    <LocalizedText textKey={TextKey.UserProfileDeleteSuccessMessage} substitutes={{ name: fullName }} />
                ),
            });
        },
        onError: (error: Error) => {
            showErrorAlert({
                description: error.message ? (
                    <>{error.message}</>
                ) : (
                    <LocalizedText textKey={TextKey.UserProfileDeleteError} substitutes={{ name: fullName }} />
                ),
            });
        },
        meta: {
            disableDefaultErrorHandler: true,
        },
        invalidateQueries: { userProfiles: ['getAll'] },
    });

const useDeleteUserProfiles = ({ fullName }: { fullName: string }) =>
    useRepositoryMutation('userProfiles', 'delete', {
        transformParams: (user: UserProfileResult) => ({ userIds: [user.userId] }),
        onSuccess: () => {
            showSuccessAlert({
                description: (
                    <LocalizedText textKey={TextKey.UserProfileDeleteSuccessMessage} substitutes={{ name: fullName }} />
                ),
            });
        },
        meta: {
            disableDefaultErrorHandler: true,
        },
        onError: (error: Error) => {
            showErrorAlert({
                description: error.message ? (
                    <>{error.message}</>
                ) : (
                    <LocalizedText textKey={TextKey.UserProfileDeleteError} substitutes={{ name: fullName }} />
                ),
            });
        },
        invalidateQueries: { userProfiles: ['getAll'] },
    });

export const useDeleteMutation = ({
    userProfile,
    onSuccess,
    onSettled,
}: {
    userProfile: UserProfileResult;
    onSuccess?: () => void;
    onSettled?: () => void;
}) => {
    const { userType } = useUserContext();

    const userProfilesMutation = useDeleteUserProfiles({ fullName: userProfile.fullName || '' });
    const enterpriseProfileMutation = useEnterpriseProfilesDelete({ fullName: userProfile.fullName || '' });

    const deleteUserProfile = () => {
        userProfilesMutation.mutate(userProfile, {
            onSettled: () => onSettled?.(),
            onSuccess: () => onSuccess?.(),
        });
    };

    const deleteEnterpriseProfile = () => {
        if (!userProfile.enterpriseProfileIds?.[0]) {
            return;
        }

        enterpriseProfileMutation.mutate(userProfile.enterpriseProfileIds, {
            onSettled: () => onSettled?.(),
            onSuccess: () => onSuccess?.(),
        });
    };

    if (userType === UserType.Properties) {
        return {
            deleteMutation: deleteUserProfile,
            isPending: userProfilesMutation.isPending,
        };
    }

    return {
        deleteMutation: deleteEnterpriseProfile,
        isPending: enterpriseProfileMutation.isPending,
    };
};
